import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Copy, ExternalLink, Rss, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { useToast } from '@/components/ui/use-toast'

interface RssFeedItem {
    guid: string
    title: string
    link: string
    pubDate: string
    description: string
    content?: string
}

interface RssFeedData {
    title: string
    description: string
    link: string
    items: RssFeedItem[]
}

export function RssFeedViewer() {
    const { id: brandId } = useParams<{ id: string }>()
    const [feedData, setFeedData] = useState<RssFeedData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const { toast } = useToast()
    
    const workerUrl = import.meta.env.VITE_WORKERS_URL
    const feedUrl = `${workerUrl}/blog-rss/${brandId}`

    const fetchRssFeed = async () => {
        if (!brandId) return
        
        setLoading(true)
        setError(null)
        
        try {
            const response = await fetch(feedUrl)
            if (!response.ok) {
                throw new Error('Failed to fetch RSS feed')
            }
            
            const xmlText = await response.text()
            const parser = new DOMParser()
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
            
            // Check for parse errors
            const parseError = xmlDoc.querySelector('parsererror')
            if (parseError) {
                throw new Error('Invalid RSS feed format')
            }
            
            // Extract feed metadata
            const channel = xmlDoc.querySelector('channel')
            if (!channel) {
                throw new Error('No channel found in RSS feed')
            }
            
            const feedTitle = channel.querySelector('title')?.textContent || ''
            const feedDescription = channel.querySelector('description')?.textContent || ''
            const feedLink = channel.querySelector('link')?.textContent || ''
            
            // Extract feed items
            const items: RssFeedItem[] = []
            const itemElements = channel.querySelectorAll('item')
            
            itemElements.forEach(item => {
                const guid = item.querySelector('guid')?.textContent || ''
                const title = item.querySelector('title')?.textContent || ''
                const link = item.querySelector('link')?.textContent || ''
                const pubDate = item.querySelector('pubDate')?.textContent || ''
                const description = item.querySelector('description')?.textContent || ''
                const content = item.querySelector('content\\:encoded')?.textContent || 
                               item.querySelector('encoded')?.textContent || ''
                
                items.push({
                    guid,
                    title,
                    link,
                    pubDate,
                    description,
                    content
                })
            })
            
            setFeedData({
                title: feedTitle,
                description: feedDescription,
                link: feedLink,
                items
            })
        } catch (err) {
            console.error('Error fetching RSS feed:', err)
            setError(err instanceof Error ? err.message : 'Failed to load RSS feed')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchRssFeed()
    }, [brandId])

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(feedUrl)
            toast({
                title: 'Copied!',
                description: 'RSS feed URL copied to clipboard',
            })
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Failed to copy URL',
                variant: 'destructive',
            })
        }
    }

    const formatPubDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'PPP')
        } catch {
            return dateString
        }
    }

    const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        return doc.body.textContent || ''
    }

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Rss className="h-6 w-6" />
                        RSS Feed
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        View and manage your brand's RSS feed
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                    >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Feed URL
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(feedUrl, '_blank')}
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Raw XML
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchRssFeed}
                        disabled={loading}
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Feed URL */}
            <Card>
                <CardContent className="p-6">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">Feed URL:</span>
                        </div>
                        <code className="block text-sm bg-muted px-3 py-2 rounded break-all">
                            {feedUrl}
                        </code>
                    </div>
                </CardContent>
            </Card>

            {/* Feed Content */}
            {loading ? (
                <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-32 w-full" />
                </div>
            ) : error ? (
                <Card className="border-destructive">
                    <CardContent className="p-6 pt-6">
                        <p className="text-destructive text-center">{error}</p>
                    </CardContent>
                </Card>
            ) : feedData ? (
                <div className="space-y-6">
                    {/* Feed Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{feedData.title}</CardTitle>
                            <CardDescription>{feedData.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted-foreground">
                                {feedData.items.length} {feedData.items.length === 1 ? 'item' : 'items'} in feed
                            </p>
                        </CardContent>
                    </Card>

                    {/* Feed Items */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold">Feed Preview</h3>
                        {feedData.items.length === 0 ? (
                            <Card>
                                <CardContent className="p-6 pt-6">
                                    <p className="text-center text-muted-foreground">
                                        No items published to RSS feed yet
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            feedData.items.map((item) => (
                                <Card key={item.guid}>
                                    <CardHeader>
                                        <div className="flex justify-between items-start gap-2">
                                            <CardTitle className="text-lg">
                                                {item.title}
                                            </CardTitle>
                                            {item.guid && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => window.open(`/blog/${item.guid}`, '_blank')}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <CardDescription>
                                            {formatPubDate(item.pubDate)}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm line-clamp-3">
                                            {stripHtml(item.description || item.content || '')}
                                        </p>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    )
}